/* eslint-disable import/no-anonymous-default-export */
import { Card, Stack } from '@mui/material';
import {
    CreateButton,
    ListBase,
    ListToolbar,
    Pagination,
    SortButton,
    Title,
    TopToolbar,
    useGetIdentity,
    useListContext,
} from 'react-admin';

import {
    EnsureDefaultCurrentUserFilter,
    isAdministrator,
} from '@/components/filters/CreatedByUserFilter';
import { QuoteEmpty } from './QuoteEmpty';
import { QuoteJobFolderListContent } from './QuoteJobFolderListContent';
import { QuoteJobFolderListFilter } from './QuoteJobFolderListFilter';

const QUOTING_FOLDERS_FILTER = { phase: 'quoting' };

type IdentityWithAdmin = {
    id: string | number;
    administrator?: boolean;
};

export const QuoteList = () => {
    const { identity } = useGetIdentity();

    if (!identity) return null;

    const current = identity as IdentityWithAdmin;
    const isAdmin = isAdministrator(current);

    return (
        <ListBase
            resource="projects"
            storeKey="quote.jobFolders"
            // Parent already waits on identity. If ListBase also gates on
            // auth/canAccess, the query is briefly disabled (data=null,
            // isFetching=false) and ra-core's empty-page effect forces page=1
            // even after a valid deep-page response (e.g. Content-Range …/743).
            disableAuthentication
            filter={QUOTING_FOLDERS_FILTER}
            filterDefaultValues={
                isAdmin ? undefined : { created_by: current.id }
            }
            perPage={25}
            sort={{ field: 'project_no', order: 'DESC' }}
        >
            <QuoteJobFolderListLayout />
        </ListBase>
    );
};

const QuoteJobFolderListLayout = () => {
    const { data, isPending, filterValues } = useListContext();
    const { identity } = useGetIdentity();

    const isAdmin = isAdministrator(identity);
    // Default created_by for non-admins shouldn't count as an "extra" filter for empty state.
    const stickyFilterKeys = isAdmin ? ['phase'] : ['phase', 'created_by'];
    const hasFilters = Object.keys(filterValues || {}).some(
        key => !stickyFilterKeys.includes(key),
    );

    return (
        <>
            <EnsureDefaultCurrentUserFilter />
            <Stack direction={{ xs: 'column', md: 'row' }} sx={{ width: '100%' }}>
                <QuoteJobFolderListFilter />
                <Stack sx={{ width: '100%', minWidth: 0, flex: 1 }}>
                    <Title title="Quote job folders" />
                    <ListToolbar actions={<QuoteJobFolderListActions />} />
                    {!identity || isPending ? null : !data?.length && !hasFilters ? (
                        <QuoteEmpty />
                    ) : (
                        <>
                            <Card>
                                <QuoteJobFolderListContent />
                            </Card>
                            <Pagination rowsPerPageOptions={[10, 25, 50, 100]} />
                        </>
                    )}
                </Stack>
            </Stack>
        </>
    );
};

const QuoteJobFolderListActions = () => (
    <TopToolbar>
        <SortButton fields={['project_no', 'name', 'id']} />
        <CreateButton
            resource="quotes"
            variant="contained"
            label="New Quote"
            sx={{ marginLeft: 2 }}
        />
    </TopToolbar>
);
